import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useAuth } from "@clerk/clerk-react";

import { ReconciliationService } from "@/api";
import type { reconciliation_DebtorPaymentRegisterResponse } from "@/api";

/** Pagination params for the per-debtor cash-in register (issue #761). */
export interface DebtorPaymentsParams {
  limit?: number;
  offset?: number;
}

/**
 * Query-key factory for the per-debtor cash-in register. Scoped by orgId so a
 * Clerk org switch does not surface another tenant's cached register. Phase 2
 * mutations (manual add / ignore / release) invalidate `debtorPaymentKeys.all`.
 */
export const debtorPaymentKeys = {
  all: ["reconciliation", "debtor-payments"] as const,
  list: (
    orgId: string | null | undefined,
    debtorId: string | null | undefined,
    params: DebtorPaymentsParams,
  ) =>
    [
      ...debtorPaymentKeys.all,
      orgId ?? null,
      debtorId ?? null,
      params,
    ] as const,
};

/**
 * useDebtorPayments loads one page of a debtor's cash-in register (every
 * inbound payment with its disposition + totals footer). Disabled until a
 * debtorId is supplied; keeps the previous page visible while fetching the next.
 *
 * FRESHNESS POLICY (#1338) — see the FRESHNESS POLICY block at the top of
 * `useDebtorAccountsList.ts` for the reasoning; this records this hook's call.
 *
 * Focus refetch is ON. The load objection that keeps it off the org-wide
 * register does not apply here: every arm of this query is filtered to one
 * `debtor_account_id`, so the cost is bounded by one family's payment history
 * and does not grow with the org's. This is money an operator acts on — the
 * per-debtor register is where a payment is released or applied — so a
 * correction made in another tab, by another operator, or by a backend sweep
 * must not survive a tab-return.
 *
 * staleTime is written out rather than inherited so the window reads as chosen.
 * The value is the app default; it is unchanged by this issue, and it is what
 * bounds the refetch above to at most one per five minutes per tab-return.
 */
export function useDebtorPayments(
  debtorId: string | null | undefined,
  params: DebtorPaymentsParams = {},
) {
  const { orgId } = useAuth();
  return useQuery<reconciliation_DebtorPaymentRegisterResponse>({
    queryKey: debtorPaymentKeys.list(orgId, debtorId, params),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
    queryFn: () =>
      ReconciliationService.getApiV1ReconciliationDebtorAccountsPayments(
        debtorId as string,
        params.limit,
        params.offset,
      ),
    enabled: !!debtorId,
    placeholderData: keepPreviousData,
  });
}
